/* Kindra Elata: isolated product controller. Native Impact owns real cart additions. */
(() => {
  'use strict';
  if (customElements.get('kindra-elata-product')) return;
  class KindraElataProduct extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      // Keep product information sections mutually exclusive, using Impact's transitions.
      this.addEventListener('toggle', event => {
        const opened = event.target;
        if (!opened.matches?.('details.ke__product-accordion') || !opened.open) return;
        this.querySelectorAll('details.ke__product-accordion').forEach(other => {
          if (other !== opened && other.open) other.open = false;
        });
      }, { capture: true, signal: this.abort.signal });

      this.addEventListener('click', event => {
        const link = event.target.closest('[data-ke-contact-scroll]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const destination = document.getElementById(link.hash.slice(1));
        if (!destination) return;
        event.preventDefault();
        this.contactScroll?.abort();
        const controller = this.contactScroll = new AbortController();
        let frame;
        controller.signal.addEventListener('abort', () => cancelAnimationFrame(frame), { once: true });
        ['wheel', 'touchstart', 'keydown'].forEach(type => window.addEventListener(type, () => controller.abort(), { passive: true, once: true, signal: controller.signal }));
        const start = window.scrollY;
        const end = Math.max(0, Math.min(start + destination.getBoundingClientRect().top - 90, document.documentElement.scrollHeight - window.innerHeight));
        const finish = () => {
          controller.abort();
          destination.setAttribute('tabindex', '-1');
          destination.setAttribute('data-ke-scroll-target', '');
          destination.focus({ preventScroll: true });
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          window.scrollTo({ top: end, behavior: 'instant' });
          finish();
          return;
        }
        let started;
        const tick = now => {
          if (controller.signal.aborted) return;
          started ??= now;
          const progress = Math.min((now - started) / 450, 1);
          const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
          window.scrollTo({ top: start + (end - start) * eased, behavior: 'instant' });
          if (progress < 1) frame = requestAnimationFrame(tick);
          else finish();
        };
        frame = requestAnimationFrame(tick);
      }, { signal: this.abort.signal });
      this.model = window.KindraElataModel;
      this.order = this.querySelector('[data-ke-order]');
      this.form = this.order.form;
      this.dialog = this.querySelector('[data-ke-dialog]');
      this.compareDialog = this.querySelector('[data-ke-compare]');
      this.assemblyDialog = this.querySelector('[data-ke-assembly-info]');
      this.deliveryDialog = this.querySelector('[data-ke-delivery-info]');
      this.finishesDialog = this.querySelector('[data-ke-finishes]');
      this.sizesDialog = this.querySelector('[data-ke-sizes]');
      this.quoteDialog = this.querySelector('[data-ke-quote]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.compareDialog.addEventListener('click', event => {
        const summary = event.target.closest('.ke__compare-disclosure > summary');
        if (!summary) return;
        event.preventDefault();
        this.deliveryAnimations ||= new Map();
        const details = summary.parentElement;
        const opening = !(this.deliveryAnimations.get(details)?.opening ?? details.open);
        if (opening) this.compareDialog.querySelectorAll('.ke__compare-disclosure[open]').forEach(other => {
          if (other !== details) this.animateDeliveryDetails(other, false);
        });
        this.animateDeliveryDetails(details, opening);
      }, { signal: this.abort.signal });
      // Route draft cart review separately from verified deposit checkout.
      this.addEventListener('submit', event => this.onSubmit(event), { capture: true, signal: this.abort.signal });
      try {
        this.data = JSON.parse(this.querySelector('[data-ke-product]').textContent);
        this.deliveryResult = window.KindraDeliveryWindows?.getResult({ productId: String(this.data.id) }) || null;
        window.addEventListener('kindra:delivery-result', event => {
          const result = event.detail;
          if (!result || String(result.configuration?.productId || '') !== String(this.data.id)) return;
          this.deliveryResult = result;
          this.deliveryPending = !this.deliveryMatchesConfiguration(result);
          if (this.deliveryPending) this.notifyDeliveryConfiguration(true);
          this.updateConfigurationPrice();
          if (this.dialog.open) this.renderSummary();
        }, { signal: this.abort.signal });
        this.variants = this.model.buildVariants(this.data.variants, this.data.options, this.data.optionNames, this.data.fallbackFinish, this.data.heaterRegion, this.data.expertAssemblyAvailable);
        const requestedVariant = this.model.buildVariants(this.data.variants, this.data.options, this.data.optionNames, this.data.fallbackFinish, this.data.heaterRegion).find(v => String(v.id) === String(this.data.selectedId));
        const selfAssemblyMatch = requestedVariant && this.data.expertAssemblyAvailable === false ? this.model.findVariant(this.variants, { ...requestedVariant.config, assembly: false }) : null;
        const initial = this.variants.find(v => String(v.id) === String(this.data.selectedId)) || selfAssemblyMatch || this.variants.find(v => v.available) || this.variants[0];
        if (!initial) throw new Error('The product option mapping needs to be completed. Please contact us to configure your sauna.');
        const remembered = this.readEditorState().selection;
        if (remembered && this.data.expertAssemblyAvailable === false && !this.data.quoteAssembly) remembered.assembly = false;
        this.state = remembered && this.model.findVariant(this.variants, this.data.quoteAssembly ? { ...remembered, assembly: false } : remembered) ? { ...remembered } : { ...initial.config };
        this.powers = { harvia: this.basePower('harvia'), huum: this.basePower('huum'), [this.state.heater]: this.state.power };
        this.money = cents => new Intl.NumberFormat(this.data.locale || 'en-AU', { style: 'currency', currency: this.data.currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
        const startingPrices = this.variants.filter(item => !item.previewOnly).map(item => item.price).filter(Number.isFinite);
        if (startingPrices.length) this.setText('[data-ke-starting-price]', this.money(Math.min(...startingPrices)));
        this.querySelectorAll('[data-ke-controls]').forEach(controls => { controls.hidden = false; });
        // App embeds may intercept or replace product-form submit controls.
        // Draft review adds exact catalog variants to Shopify's shared cart.
        this.addEventListener('click', event => {
          // Let Cowlendar handle its calendar links and open the native booking popup.
          if (event.target.closest('[data-ke-book-call]')) {
            const dialog = event.target.closest('dialog[open]');
            if (dialog) dialog.close();
            return;
          }
          if (this.data.purchaseEnquiry) {
            const enquiry = event.target.closest('[data-ke-enquire]');
            const options = event.target.closest('[data-ke-sticky-order]');
            if ((enquiry || options) && this.contains(enquiry || options)) {
              event.preventDefault(); event.stopImmediatePropagation();
              if (enquiry) this.openPurchaseEnquiry(enquiry);
              else this.showPurchaseOptions();
              return;
            }
          }
          const action = event.target.closest('[data-ke-order], [data-ke-sticky-order]');
          if (this.data.quoteMode && action && this.contains(action)) {
            event.preventDefault(); event.stopImmediatePropagation();
            this.requestOrder(action);
            return;
          }
          if (this.data.checkoutEnabled || !action || !this.contains(action)) return;
          event.preventDefault(); event.stopImmediatePropagation();
          if (!action.disabled) this.addConfigurationToCart();
        }, { capture: true, signal: this.abort.signal });
        this.addEventListener('click', event => this.onClick(event), { signal: this.abort.signal });
        window.addEventListener('popstate', () => {
          const id = new URL(window.location.href).searchParams.get('variant');
          const variant = this.variants.find(v => String(v.id) === id);
          if (variant) {
            this.state = { ...variant.config };
            this.powers[this.state.heater] = this.state.power;
            this.render(false);
          }
        }, { signal: this.abort.signal });
        [this.dialog, this.compareDialog, this.assemblyDialog, this.deliveryDialog, this.finishesDialog, this.sizesDialog, this.quoteDialog].filter(Boolean).forEach(dialog => {
          dialog.addEventListener('close', () => {
            if (this.isConnected && this.data?.editorMode) { window.KindraElataEditorDrawer = null; this.writeEditorState({ drawer: null }); }
            this.restoreScroll(); this.syncForms(); this.updateMobileSticky();
            this.returnFocus?.focus({ preventScroll: true });
          }, { signal: this.abort.signal });
          dialog.addEventListener('click', event => {
            if (event.target !== dialog) return;
            const box = dialog.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
          }, { signal: this.abort.signal });
        });
        this.form.addEventListener('cart:error', event => this.showError(event.detail.error || 'Unable to add your configuration. Please try again.'), { signal: this.abort.signal });
        this.resize = new ResizeObserver(() => this.updateStickyPosition());
        this.resize.observe(this.querySelector('[data-ke-panel]'));
        window.addEventListener('resize', () => this.updateStickyPosition(), { passive: true, signal: this.abort.signal });
        // Match the HTML concept: available above and below the main CTA,
        // hidden while that CTA is visible or the summary is open.
        this.visibility = new IntersectionObserver(() => this.updateMobileSticky(), { rootMargin: '12px 0px', threshold: 0 });
        this.visibility.observe(this.order);
        this.setupDeliveryCompatibility();
        this.setupForms();
        this.render(!!selfAssemblyMatch);
        this.setupEditor();
        this.setupQuote();
        if (this.querySelector('kindra-delivery-assembly')) customElements.whenDefined('kindra-delivery-assembly').then(() => {
          if (!this.isConnected) return;
          this.deliveryAssembly = this.querySelector('kindra-delivery-assembly');
          this.deliveryAssembly.attach(this);
          this.render(false);
        });
      } catch (error) {
        this.showError(error.message || 'Please contact us to configure your sauna.');
        this.order.disabled = true;
      }
    }

    readEditorState() {
      if (!this.data?.editorMode) return {};
      try { return JSON.parse(sessionStorage.getItem('kindra-editor:' + this.dataset.sectionId + ':' + this.data.id) || '{}'); }
      catch { return {}; }
    }

    writeEditorState(changes) {
      if (!this.data?.editorMode) return;
      try { sessionStorage.setItem('kindra-editor:' + this.dataset.sectionId + ':' + this.data.id, JSON.stringify({ ...this.readEditorState(), ...changes })); }
      catch { /* The editor also works when browser storage is unavailable. */ }
    }

    setupEditor() {
      if (!this.data.editorMode) return;
      const savedDrawer = this.readEditorState().drawer;
      if (savedDrawer) window.KindraElataEditorDrawer = { section: this.dataset.sectionId, block: this.data.blockIds[savedDrawer], type: savedDrawer };
      this.addEventListener('shopify:block:select', event => {
        const selected = event.detail?.blockId;
        const target = [...this.querySelectorAll('[data-ke-block-id]')].find(node => node.dataset.keBlockId === selected);
        target?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        // Keep an already-open comparison visible through editor setting reloads.
        const remembered = window.KindraElataEditorDrawer;
        if (remembered?.section === this.dataset.sectionId && remembered.block === selected) this.restoreEditorDrawer(remembered);
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const pairs = [['data-ke-finishes-open', 'finish'], ['data-ke-compare-open', 'heater'], ['data-ke-assembly-open', 'assembly'], ['data-ke-delivery-open', 'delivery']];
        for (const [attribute, type] of pairs) {
          if (event.target.closest('[' + attribute + ']')) {
            window.KindraElataEditorDrawer = { section: this.dataset.sectionId, block: this.data.blockIds[type], type };
            this.writeEditorState({ drawer: type });
          }
        }
        if (event.target.closest('[data-ke-finishes-close], [data-ke-compare-close], [data-ke-assembly-close], [data-ke-delivery-close]')) window.KindraElataEditorDrawer = null;
      }, { signal: this.abort.signal });
      const remembered = window.KindraElataEditorDrawer;
      if (remembered?.section === this.dataset.sectionId) this.restoreEditorDrawer(remembered);
    }

    restoreEditorDrawer(remembered) {
      if (this.data.blockIds[remembered.type] !== remembered.block) return;
      const dialogs = { finish: this.finishesDialog, heater: this.compareDialog, assembly: this.assemblyDialog, delivery: this.deliveryDialog };
      const dialog = dialogs[remembered.type];
      if (dialog && !dialog.open) {
        if (remembered.type === 'heater') this.renderComparison();
        this.openDialog(dialog, null);
      }
    }

    disconnectedCallback() {
      this.contactScroll?.abort();
      if (this.data?.editorMode && this.state) {
        window.KindraElataEditorSelection ||= {};
        window.KindraElataEditorSelection[this.dataset.sectionId] = { ...this.state };
      }
      this.abort?.abort();
      this.abort = null;
      this.deliveryObserver?.disconnect();
      this.deliveryAnimations?.forEach(({ animation }) => animation.cancel());
      this.deliveryAnimations?.clear();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.formsDiscovery?.disconnect();
      clearInterval(this.formsDiscoveryTimer);
      this.formsObserver?.disconnect();
      this.formsHost?.removeAttribute('data-ke-managed-teaser');
      this.formsHost?.removeAttribute('data-ke-suspended');
      if (this.dialog?.open) this.dialog.close();
      if (this.compareDialog?.open) this.compareDialog.close();
      if (this.assemblyDialog?.open) this.assemblyDialog.close();
      if (this.deliveryDialog?.open) this.deliveryDialog.close();
      if (this.finishesDialog?.open) this.finishesDialog.close();
      if (this.sizesDialog?.open) this.sizesDialog.close();
      this.restoreScroll();
    }

    basePower(heater) {
      if (!this.data?.highPowerOnly) return 6;
      return heater === 'harvia' && this.data.heaterRegion === 'us' ? 8 : 9;
    }

    find(changes = {}) {
      const config = { ...this.state, ...changes };
      if (this.data.quoteAssembly) config.assembly = false;
      const exact = this.model.findVariant(this.variants, config);
      if (this.data.highPowerOnly || !this.data?.visualPreview || this.data.checkoutEnabled || (this.data.heaterRegion && this.data.heaterRegion !== 'legacy')) return exact;
      if (exact) return { ...exact, available: true, selling_plan_allocations: [] };
      // Draft-only visual combinations; never represent these as Shopify variant IDs.
      const source = this.model.findVariant(this.variants, { ...config, finish: 'natural', power: 6 });
      if (!source) return null;
      return {
        ...source,
        id: 'preview-' + [config.finish, config.heater, config.power, config.assembly].join('-'),
        sourceVariantId: source.id,
        previewOnly: true,
        config,
        available: true,
        price: source.price + (config.finish === 'ash' ? 150000 : 0) + (config.power === 9 ? 20000 : 0),
        featured_media: {},
        selling_plan_allocations: []
      };
    }
    label(finish) { return this.data?.labels?.[finish] || (finish === 'ash' ? 'Ash Edition' : 'Natural'); }
    galleryFinish(finish) { return finish === 'ash' ? 'Ash Edition' : 'Natural'; }
    assemblyLabel(assembled) { return this.data?.labels?.[assembled ? 'expert' : 'self'] || (assembled ? 'Expert Assembly' : 'Self Assembly'); }
    heaterName(type) { return this.data?.labels?.[type] || (type === 'huum' ? 'HUUM Drop' : 'Harvia The Wall'); }
    setText(selector, value) {
      const target = this.querySelector(selector);
      if (!target) return;
      if (selector === '[data-ke-total]' || selector === '[data-ke-sticky-price]') this.crossfadePrice(target, value);
      else target.textContent = value;
    }
    crossfadePrice(target, value) {
      if (target.textContent === value) return;
      const previous = target.textContent;
      cancelAnimationFrame(target._priceFrame);
      target._priceAnimations?.forEach(animation => animation.cancel());
      target._priceAnimations = [];
      const numberFormat = new Intl.NumberFormat(this.data.locale || 'en-AU', { maximumFractionDigits: 0 });
      const parts = new Intl.NumberFormat(this.data.locale || 'en-AU').formatToParts(12345.6);
      const group = parts.find(part => part.type === 'group')?.value || ',';
      const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
      const readAmount = text => {
        const match = text.match(/[0-9][0-9.,\u00a0\u202f ]*/);
        return match ? Number(match[0].split(group).join('').replace(decimal, '.').trim()) : NaN;
      };
      const from = target._priceDisplayed ?? readAmount(previous);
      const to = readAmount(value);
      target._priceDisplayed = undefined;
      if (!previous || !Number.isFinite(from) || !Number.isFinite(to) || from === to ||
          typeof target.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !target.getClientRects().length) {
        target.textContent = value;
        return;
      }
      // Keep the real, accessible amount exact immediately. Only the decorative overlay counts.
      const current = document.createElement('span');
      current.textContent = value;
      current.style.opacity = '0';
      const display = document.createElement('span');
      display.className = 'ke-price-outgoing';
      display.setAttribute('aria-hidden', 'true');
      const format = amount => value.replace(/[0-9][0-9.,\u00a0\u202f]*/, numberFormat.format(Math.round(amount)));
      display.dataset.price = format(from);
      target.classList.add('ke-price-crossfade');
      target.replaceChildren(current, display);
      target._priceAnimations = [display.animate([{ opacity: 0.65 }, { opacity: 1 }], { duration: 350, easing: 'ease-out' })];
      let started;
      const tick = now => {
        if (current.parentNode !== target) return;
        started ??= now;
        const progress = Math.min((now - started) / 350, 1);
        const amount = from + (to - from) * (1 - Math.pow(1 - progress, 3));
        target._priceDisplayed = amount;
        display.dataset.price = format(amount);
        if (progress < 1) target._priceFrame = requestAnimationFrame(tick);
        else {
          target.textContent = value;
          target._priceDisplayed = undefined;
          target._priceFrame = null;
          target._priceAnimations = [];
        }
      };
      target._priceFrame = requestAnimationFrame(tick);
    }
    increment(price, base) { return price === base ? 'Included' : `${price > base ? '+ ' : '− '}${this.money(Math.abs(price - base))}`; }
    showError(message) {
      const element = this.querySelector('[data-ke-error]');
      element.textContent = typeof message === 'string' ? message : 'Unable to complete your request.';
      element.hidden = !message;
    }

    select(changes) {
      // An unavailable combination must never silently reset another choice.
      if (!this.find(changes)) return;
      const changingHeater = changes.heater && changes.heater !== this.state.heater;
      const cards = !changingHeater && this.reducedMotion?.matches === false
        ? [...this.querySelectorAll('[data-ke-heater-card]')].map(card => {
          const height = card.getBoundingClientRect().height;
          card._keHeightAnimation?.cancel();
          return { card, height };
        }) : [];
      const revealed = [...this.querySelectorAll('[data-ke-finish-copy], [data-ke-electrical]')].filter(node => node.hidden);
      this.state = { ...this.state, ...changes };
      this.powers[this.state.heater] = this.state.power;
      this.render(true);
      if (this.reducedMotion?.matches === false) revealed.filter(node => !node.hidden && node.animate).forEach(node => {
        node._keReveal?.cancel();
        node._keReveal = node.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
      });
      cards.forEach(({ card, height }) => {
        if (!card.animate) return;
        const end = card.getBoundingClientRect().height;
        const animation = card.animate(
          [{ height: height + 'px', overflow: 'hidden' }, { height: end + 'px', overflow: 'hidden' }],
          { duration: 300, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
        card._keHeightAnimation = animation;
        animation.onfinish = animation.oncancel = () => {
          if (card._keHeightAnimation === animation) card._keHeightAnimation = null;
        };
      });
    }


    renderHeaterChoices() {
      const base = this.find({ heater: 'harvia', power: this.basePower('harvia'), assembly: false });
      const huumBase = this.find({ heater: 'huum', power: this.basePower('huum'), assembly: false });
      this.setText('[data-ke-heater-starting-price]', huumBase && base ? `${this.data.highPowerOnly ? '' : 'From '}${this.increment(huumBase.price, base.price)}` : '');
      this.querySelectorAll('[data-ke-heater]').forEach(button => {
        const heater = button.dataset.keHeater;
        const active = heater === this.state.heater;
        const power = this.powers[heater];
        const candidate = this.find({ heater, power });
        button.setAttribute('aria-pressed', String(active));
        button.disabled = !candidate || (!candidate.available && !this.data?.quoteMode);
        this.setText(`[data-ke-heater-name="${heater}"]`, this.heaterName(heater));
        this.querySelector(`[data-ke-heater-card="${heater}"]`).toggleAttribute('data-selected', active);
      });
      this.querySelectorAll('[data-ke-power]').forEach(button => {
        const heater = button.dataset.kePowerHeater;
        const power = Number(button.dataset.kePower);
        const candidate = this.find({ heater, power });
        const unassembled = this.find({ heater, power, assembly: false });
        button.disabled = !candidate || (!candidate.available && !this.data?.quoteMode);
        button.setAttribute('aria-pressed', String(heater === this.state.heater && power === this.state.power));
        button.querySelector('[data-ke-power-benchmark]').textContent = this.data.benchmarks[heater + power] || '';
        const cost = button.querySelector('[data-ke-power-cost]');
        cost.textContent = unassembled && base ? this.increment(unassembled.price, base.price) : '';
        cost.hidden = !cost.textContent;
      });
    }

    renderAssemblyChoices() {
      if (this.deliveryAssembly?.parentController) {
        this.querySelectorAll('[data-ke-assembly]').forEach(button => {
          const assembly = button.dataset.keAssembly === 'true';
          button.setAttribute('aria-pressed', String(this.state.assembly === assembly));
          button.disabled = assembly && !this.deliveryAssembly.assemblyAllowed();
        });
        this.deliveryAssembly.refresh();
        return;
      }
      this.querySelectorAll('[data-ke-assembly]').forEach(button => {
        const assembly = button.dataset.keAssembly === 'true';
        button.setAttribute('aria-pressed', String(this.state.assembly === assembly));
        const candidate = this.find({ assembly });
        button.disabled = (assembly && this.data?.expertAssemblyAvailable === false) || !candidate || (!candidate.available && !this.data?.quoteMode);
      });
      const assembled = this.find({ assembly: true });
      const unassembled = this.find({ assembly: false });
      this.setText('[data-ke-assembly-cost]', this.data?.quoteAssembly ? 'Quoted separately' : assembled && unassembled ? this.increment(assembled.price, unassembled.price) : 'Unavailable');
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.hasAttribute('data-ke-finish')) this.select({ finish: button.dataset.keFinish });
      if (button.hasAttribute('data-ke-heater')) this.select({ heater: button.dataset.keHeater, power: this.powers[button.dataset.keHeater] });
      if (button.hasAttribute('data-ke-power')) this.select({ heater: button.dataset.kePowerHeater || this.state.heater, power: Number(button.dataset.kePower) });
      if (button.hasAttribute('data-ke-assembly')) this.select({ assembly: button.dataset.keAssembly === 'true' });
      if (button.hasAttribute('data-ke-sticky-order')) this.form.requestSubmit(this.order);
      if (button.hasAttribute('data-ke-close')) this.dialog.close();
      if (button.hasAttribute('data-ke-compare-open')) {
        this.renderComparison();
        this.openDialog(this.compareDialog, button);
      }
      if (button.hasAttribute('data-ke-sizes-open')) this.openDialog(this.sizesDialog, button);
      if (button.hasAttribute('data-ke-sizes-close')) this.sizesDialog?.close();
      if (button.hasAttribute('data-ke-finishes-open')) this.openDialog(this.finishesDialog, button);
      if (button.hasAttribute('data-ke-finishes-close')) this.finishesDialog.close();
      if (button.hasAttribute('data-ke-assembly-open')) this.openDialog(this.assemblyDialog, button);
      if (button.hasAttribute('data-ke-assembly-close')) this.assemblyDialog.close();
      if (button.hasAttribute('data-ke-delivery-open') && this.deliveryDialog) this.openDialog(this.deliveryDialog, button);
      if (button.hasAttribute('data-ke-delivery-close')) this.deliveryDialog?.close();
      if (button.hasAttribute('data-ke-compare-close')) this.compareDialog.close();
      if (button.hasAttribute('data-ke-offer-open')) {
        const tray = document.querySelector('kindra-welcome-offer');
        if (tray?.openOffer) {
          tray.openOffer();
          tray.offerTrigger = button;
        } else {
          this.formsHost?.removeAttribute('data-ke-managed-teaser');
          this.formsHost?.shadowRoot?.querySelector('[data-testid="form-teaser"]')?.click();
        }
      }
      if (button.hasAttribute('data-ke-edit-sauna')) {
        this.returnFocus = this.querySelector('[data-ke-finish][aria-pressed="true"]');
        this.dialog.close();
        // A tall desktop sticky panel can keep its early selectors above the
        // viewport even after scrollIntoView on the selector itself. Return to
        // the section's normal-flow origin before focusing those controls.
        const destination = window.matchMedia('(min-width: 1000px)').matches ? this : this.returnFocus;
        destination?.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: window.matchMedia('(min-width: 1000px)').matches ? 'start' : 'center' });
      }
      if (button.hasAttribute('data-ke-remove-sauna')) {
        this.showSummaryState('Your Configuration', 'Your configuration has been removed.', 'Continue configuring', 'data-ke-edit-sauna');
      }
      if (button.hasAttribute('data-ke-preview-checkout')) {
        // This button is part of the configuration preview only. It never submits a cart or payment request.
        this.showSummaryState('Checkout preview', 'This is a checkout preview. No order has been placed and no payment has been taken.', 'Back to your configuration', 'data-ke-back-summary', this.due);
      }
      if (button.hasAttribute('data-ke-back-summary')) {
        this.renderSummary();
        this.querySelector('[data-ke-preview-checkout]').focus();
      }
    }

    removeSummaryOption(changes, focusRow, message) {
      if (!this.find(changes)) return;
      this.select(changes);
      // The old Remove button was replaced with the refreshed summary. Keep
      // focus at the changed item (or the next row), rather than jumping to Close.
      const target = this.querySelector(`[data-ke-summary-line="${focusRow}"]`) || this.querySelector('[data-ke-summary-title]');
      if (target) { target.tabIndex = -1; target.focus({ preventScroll: true }); }
      const status = this.querySelector('[data-ke-summary-status]');
      if (status) status.textContent = `${message} Deposit due today ${this.money(this.due)}.`;
    }

    renderFinishPrice() {
      const natural = this.find({ finish: 'natural' });
      const ash = this.find({ finish: 'ash' });
      this.setText('[data-ke-finish-cost]', natural && ash ? this.increment(ash.price, natural.price) : 'Unavailable');
    }

    render(updateUrl) {
      const assemblyModule = this.querySelector('kindra-delivery-assembly');
      if (assemblyModule?.parentController && !assemblyModule.assemblyAllowed()) this.state.assembly = false;
      this.writeEditorState({ selection: this.state });
      const previousVariant = this.variant;
      this.variant = this.find();
      const variant = this.variant;
      this.deposit = this.model.getDeposit(variant, this.data.planId);
      const total = this.deposit?.total ?? variant?.price;
      const due = this.deposit?.due ?? Math.round(total * 0.3);
      this.total = total; this.due = due;
      const base = this.find({ heater: 'harvia', power: this.basePower('harvia'), assembly: false });
      this.querySelectorAll('[data-ke-finish]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.keFinish === this.state.finish));
        button.disabled = !this.find({ finish: button.dataset.keFinish });
      });
      this.querySelectorAll('[data-ke-finish-copy]').forEach(copy => { copy.hidden = copy.dataset.keFinishCopy !== this.state.finish; });
      this.renderFinishPrice();
      this.renderHeaterChoices();
      this.querySelectorAll('[data-ke-electrical]').forEach(note => {
        note.hidden = this.state.power !== 9 && this.state.power !== 8 || note.dataset.keElectrical !== this.state.heater;
      });
      this.renderAssemblyChoices();
      this.setText('[data-ke-total]', variant ? this.money(total) : 'Unavailable');
      this.setText('[data-ke-warranty]', this.state.heater === 'huum' ? '3 Year HUUM Warranty' : '2 Year Harvia Warranty');
      // Quotes can be requested before shipping is enabled; checkout gates remain unchanged.
      const purchasable = !!variant && (!!variant.available || !!this.data?.quoteMode);
      const paymentReady = this.data.quoteMode || !this.data.checkoutEnabled || !!this.deposit;
      this.order.disabled = !purchasable || !paymentReady;
      const label = !purchasable ? 'Currently unavailable' : (this.data.labels?.purchase || 'Place 30% Deposit');
      this.order.textContent = label;
      const stickyOrder = this.querySelector('[data-ke-sticky-order]');
      stickyOrder.textContent = this.data.purchaseEnquiry ? this.data.purchaseOptionsLabel : label;
      stickyOrder.disabled = this.data.purchaseEnquiry ? !variant || !!variant.previewOnly : this.order.disabled;
      const enquiry = this.querySelector('[data-ke-enquire]');
      if (enquiry) enquiry.disabled = !variant || !!variant.previewOnly;
      this.setText('[data-ke-sticky-price]', variant ? `${this.money(total)} ${this.data.currency}` : 'Unavailable');
      this.showError(this.data.checkoutEnabled && purchasable && !paymentReady ? 'Online deposits are temporarily unavailable. Please contact us to place your order.' : '');
      const idInput = this.form.querySelector('[name="id"]');
      idInput.value = variant?.previewOnly ? '' : (variant?.id || ''); idInput.disabled = !variant || !!variant.previewOnly;
      this.applyPlan();
      if (variant && previousVariant?.id !== variant.id) {
        if (!variant.previewOnly) idInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (!variant.previewOnly) this.form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { formId: this.form.getAttribute('id'), variant, previousVariant } }));
      }
      const galleryKey = this.state.finish + ':' + this.state.heater;
      if (this.galleryKey !== galleryKey) {
        this.galleryKey = galleryKey;
        const finish = this.galleryFinish(this.state.finish);
        const heater = this.state.heater;
        const mediaId = variant?.featured_media?.id;
        customElements.whenDefined('kindra-elata-gallery').then(() => {
          if (this.isConnected && this.galleryKey === galleryKey) this.querySelector('kindra-elata-gallery')?.setFinish(finish, mediaId, heater);
        });
      }
      if (updateUrl && variant && !variant.previewOnly && !window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        url.searchParams.delete('selling_plan');
        window.history.replaceState(window.history.state, '', url.toString());
      }
      this.notifyDeliveryConfiguration();
      this.readLegacyDelivery();
      this.updateConfigurationPrice();
      if (this.dialog.open) this.renderSummary();
      this.updateStickyPosition();
    }

    notifyDeliveryConfiguration(force = false) {
      if (!window.KindraDeliveryWindows || !this.variant) return;
      const detail = {
        productId: String(this.data.id),
        variantId: String(this.variant.sourceVariantId || this.variant.id),
        configuration: { ...this.state },
      };
      const signature = JSON.stringify(detail);
      if (!force && signature === this.deliveryConfigurationSignature) return;
      this.deliveryConfigurationSignature = signature;
      this.deliveryPending = !this.deliveryMatchesConfiguration(this.deliveryResult);
      window.dispatchEvent(new CustomEvent('kindra:configuration-change', { detail }));
    }

    // The installed v18 app predates the shared result API. Read its embedded
    // configuration for the selected state; never infer a rate from rendered text.
    // This adapter becomes inactive as soon as the documented API is present.
    setupDeliveryCompatibility() {
      const app = this.querySelector('kindra-delivery-windows');
      if (!app) return;
      this.deliveryAnimations = new Map();
      // Own exclusive disclosure timing so the app cannot snap siblings shut.
      app.addEventListener('toggle', event => {
        if (event.target.matches('.kdw-details')) event.stopImmediatePropagation();
      }, { capture: true, signal: this.abort.signal });
      app.addEventListener('click', event => {
        const summary = event.target.closest('.kdw-details > summary');
        if (!summary || !app.contains(summary)) return;
        event.preventDefault();
        const details = summary.parentElement;
        const opening = !(this.deliveryAnimations.get(details)?.opening ?? details.open);
        if (opening) app.querySelectorAll('.kdw-details[open]').forEach(other => {
          if (other !== details) this.animateDeliveryDetails(other, false);
        });
        this.animateDeliveryDetails(details, opening);
      }, { signal: this.abort.signal });
      this.deliveryObserver = new MutationObserver(() => {
        this.expandDeliveryLabels(app);
      this.setupNativeDeliverySelect(app);
        this.readLegacyDelivery();
        this.updateConfigurationPrice();
        if (this.dialog.open) this.renderSummary();
      });
      this.deliveryObserver.observe(app, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
      this.expandDeliveryLabels(app);
      this.setupNativeDeliverySelect(app);
      this.readLegacyDelivery();
    }

    animateDeliveryDetails(details, opening) {
      const start = details.getBoundingClientRect().height;
      this.deliveryAnimations.get(details)?.animation.cancel();
      this.deliveryAnimations.delete(details);
      if (this.reducedMotion.matches || !details.animate) {
        details.open = opening;
        details.style.removeProperty('overflow');
        return;
      }
      details.open = true;
      const end = opening ? details.getBoundingClientRect().height : details.querySelector('summary').getBoundingClientRect().height + parseFloat(getComputedStyle(details).borderTopWidth) + parseFloat(getComputedStyle(details).borderBottomWidth);
      details.style.overflow = 'hidden';
      const animation = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 180, easing: 'ease-out' });
      this.deliveryAnimations.set(details, { animation, opening });
      animation.onfinish = () => {
        details.open = opening;
        details.style.removeProperty('overflow');
        this.deliveryAnimations.delete(details);
      };
    }

    expandDeliveryLabels(app) {
      const deliveryFee = app.querySelector('.kdw-summary__fee');
      if (deliveryFee && deliveryFee.textContent.trim() === 'Complimentary Delivery') deliveryFee.textContent = 'Complimentary';

      const detailsLabel = app.querySelector('.kdw-summary__details-text');
      if (detailsLabel && detailsLabel.textContent.trim() !== 'Delivery Details') detailsLabel.textContent = 'Delivery Details';
      const summary = app.querySelector('.kdw-summary');
      if (summary && !summary.querySelector('.ke__delivery-heading')) {
        const heading = document.createElement('h2');
        heading.className = 'ke__delivery-heading';
        heading.textContent = 'Delivery';
        summary.prepend(heading);
      }
      const arrow = app.querySelector('.kdw-summary__arrow');
      const chevron = this.querySelector('[data-ke-compare-open] svg');
      if (arrow && chevron && !arrow.dataset.keChevron) {
        arrow.dataset.keChevron = 'true';
        arrow.replaceChildren(chevron.cloneNode(true));
      }
      if (chevron) app.querySelectorAll('.kdw-state-trigger__arrow').forEach(stateArrow => {
        if (stateArrow.dataset.keChevron) return;
        stateArrow.dataset.keChevron = 'true';
        stateArrow.replaceChildren(chevron.cloneNode(true));
      });
      app.querySelectorAll('.kdw-state-trigger__value').forEach(value => {
        const label = value.textContent.trim();
        const match = label.match(/^(ACT|NSW|NT|QLD|SA|TAS|VIC|WA)\s*·\s*(.+)$/);
        if (!match) return;
        // Show the full destination name without repeating its state code.
        value.closest('button')?.setAttribute('aria-label', 'Delivery to ' + match[2]);
        value.textContent = match[2];
      });
    }


    setupNativeDeliverySelect(app) {
      app.querySelectorAll('.kdw-state-picker--summary').forEach(picker => {
        const trigger = picker.querySelector('.kdw-state-trigger');
        const buttons = Array.from(picker.querySelectorAll('.kdw-state-option'));
        if (!trigger || !buttons.length) return;
        let select = picker.querySelector('.ke__delivery-select');
        if (!select) {
          select = document.createElement('select');
          select.className = 'ke__delivery-select';
          select.setAttribute('aria-label', 'Delivery state');
          select.addEventListener('change', () => {
            // Keep the app's destination, quote and persistence logic intact.
            const options = Array.from(picker.querySelectorAll('.kdw-state-option'));
            const button = options.find(option => option.querySelector('strong')?.textContent.trim() === select.value);
            if (!button || button.disabled) return;
            button.click();
            // The app may rebuild its summary after a destination change.
            this.setupNativeDeliverySelect(app);
            app.querySelector('.ke__delivery-select')?.focus({ preventScroll: true });
          }, { signal: this.abort.signal });
          picker.appendChild(select);
          picker.classList.add('ke__native-state-picker');
          trigger.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('tabindex', '-1');
        }
        const options = buttons.map(button => ({
          value: button.querySelector('strong')?.textContent.trim() || '',
          label: button.querySelector('span')?.textContent.trim() || button.textContent.trim(),
          disabled: button.disabled,
          selected: button.getAttribute('aria-pressed') === 'true'
        }));
        const signature = JSON.stringify(options.map(({ value, label, disabled }) => ({ value, label, disabled })));
        if (select.dataset.options !== signature) {
          select.replaceChildren(...options.map(item => {
            const option = document.createElement('option');
            option.value = item.value;
            option.textContent = item.label;
            option.disabled = item.disabled;
            return option;
          }));
          select.dataset.options = signature;
        }
        select.value = options.find(option => option.selected)?.value || '';
      });
    }

    readLegacyDelivery() {
      if (window.KindraDeliveryWindows?.getResult) return;
      const app = this.querySelector('kindra-delivery-windows');
      const selected = app?.querySelector('.kdw-state-option[aria-pressed="true"]');
      const name = selected?.querySelector('span')?.textContent?.trim();
      const label = app?.querySelector('.kdw-summary__fee')?.textContent?.trim();
      const code = selected?.querySelector('strong')?.textContent.trim();
      let config = {};
      try { config = JSON.parse(app?.querySelector('[data-kdw-config]')?.textContent || '{}'); } catch { /* An unknown fee stays unresolved. */ }
      const cluster = config.clusters?.find(item => item.enabled !== false && item.states?.includes(code));
      const minorUnits = this.legacyDeliveryAmount(cluster?.fee);
      this.deliveryResult = name ? {
        source: 'installed-app-config',
        destination: { code, name },
        status: /\b(?:quote|contact|assistance|required)\b/i.test(`${cluster?.fee || ''} ${cluster?.window || ''}`) ? 'assistance_required' : minorUnits === null ? 'fee_unavailable' : 'valid',
        fee: { minorUnits, currency: String(config.shippingSync?.currency || 'AUD').toUpperCase(), label: /^complimentary(?: delivery)?$/i.test(label || '') ? 'Complimentary' : label },
        displayWindow: app.querySelector('.kdw-summary__detail')?.textContent?.trim(),
        configuration: { productId: String(this.data.id), variantId: String(this.variant?.sourceVariantId || this.variant?.id || this.data.selectedId) },
      } : null;
      this.deliveryPending = false;
    }

    legacyDeliveryAmount(label) {
      const text = String(label || '').trim();
      if (/^(?:complimentary|free)(?:\s+(?:delivery|shipping))?[.!]?$/i.test(text)) return 0;
      // Only one unconditional AUD amount is safe to include. Ranges, "from",
      // discounts, quotes and foreign-currency labels remain unresolved.
      const match = text.match(/^(?:AUD\s*\$?\s*|A\$\s*|\$\s*)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?(?:\s*AUD)?(?:\s+(?:delivery|shipping))?$/i);
      if (!match) return null;
      const amount = Number(match[1].replace(/,/g, '')) * 100 + Number((match[2] || '').padEnd(2, '0'));
      return Number.isSafeInteger(amount) ? amount : null;
    }

    deliveryMatchesConfiguration(result) {
      const configuration = result?.configuration;
      if (!configuration || String(configuration.productId) !== String(this.data.id) || String(configuration.variantId) !== String(this.variant?.sourceVariantId || this.variant?.id || this.data.selectedId || '')) return false;
      return !configuration.configuration || ['finish', 'heater', 'power', 'assembly'].every(key => configuration.configuration[key] === this.state[key]);
    }

    configurationAmounts() {
      if (this.deliveryAssembly?.parentController) return this.deliveryAssembly.estimates();
      if (this.data.quoteMode) return { subtotal: this.variant?.price, delivery: null, total: this.variant?.price, due: 0, saunaBalance: 0 };
      const result = this.deliveryResult;
      const amount = result?.fee?.minorUnits;
      const known = !this.deliveryPending && !!result?.destination && this.deliveryMatchesConfiguration(result) && ['valid', 'timing_unavailable'].includes(result.status) && result.eligible !== false && result.quoteType !== 'quote_required' && Number.isSafeInteger(amount) && amount >= 0 && String(result.fee.currency || '').toUpperCase() === String(this.data.currency).toUpperCase();
      return { subtotal: this.total, delivery: known ? amount : null, total: this.total + (known ? amount : 0), due: this.due, saunaBalance: this.total - this.due };
    }

    updateConfigurationPrice() {
      if (!this.variant || !Number.isFinite(this.total)) return;
      const amounts = this.configurationAmounts();
      this.setText('[data-ke-total]', this.money(amounts.total));
      this.setText('[data-ke-total-label]', amounts.delivery === null ? (this.data.labels?.subtotal || 'Configuration subtotal') : (this.data.labels?.total || 'Your Configuration'));
      this.setText('[data-ke-sticky-price]', `${this.money(amounts.total)} ${this.data.currency}${amounts.delivery === null ? ' + delivery' : ''}`);
      const note = this.querySelector('[data-ke-delivery-note]');
      if (note && this.data.quoteMode) {
        note.hidden = false;
        note.textContent = this.data.quoteAssembly && this.state.assembly ? 'Delivery and expert assembly will be priced in your personalized quote. No payment required.' : this.data.quoteNote || 'Delivery will be included in your personalized quote. No payment required.';
        return;
      }
      if (note) {
        if (this.data.checkoutEnabled === false && this.data.heaterRegion && this.data.heaterRegion !== 'legacy') {
          note.hidden = false;
          note.textContent = 'Save your configuration in the cart. Contact Kindra to confirm delivery and payment arrangements.';
          return;
        }
        note.hidden = amounts.delivery !== null;
        note.textContent = this.deliveryPending ? 'Updating delivery…' : !this.deliveryResult?.destination ? 'Select your state to include delivery.' : 'Delivery to be confirmed separately.';
      }
    }

    deliveryFeeText(result) {
      if (result.status === 'assistance_required') return 'Quote required';
      if (result.status === 'error' || result.status === 'fee_unavailable') return 'To be confirmed';
      if (result.fee?.minorUnits === 0) return 'Complimentary';
      if (Number.isInteger(result.fee?.minorUnits)) {
        return new Intl.NumberFormat(this.data.locale || 'en-AU', {
          style: 'currency',
          currency: result.fee.currency || this.data.currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: result.fee.minorUnits % 100 ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(result.fee.minorUnits / 100);
      }
      if (result.status === 'assistance_required') return 'Quote required';
      return result.fee?.label || 'Confirmed after review';
    }

    appendDeliverySummary(addLine) {
      if (this.deliveryPending || (this.deliveryResult && !this.deliveryMatchesConfiguration(this.deliveryResult))) {
        addLine('Delivery', 'Updating…');
        return;
      }
      const result = this.deliveryResult;
      if (!result?.destination) { addLine('Delivery', 'Select your state'); return; }
      addLine(`Delivery to ${result.destination.code || result.destination.name}`, this.deliveryFeeText(result));
      const windowLabel = (result.displayWindow || result.window || '').replace(/^(?:delivers|estimated(?: delivery)?(?: window)?|delivery window)\s*:?\s*/i, '').trim();
      if (windowLabel) addLine('Delivery window', windowLabel);
    }

    applyPlan() {
      const input = this.querySelector('[data-ke-plan]');
      input.disabled = !this.data.checkoutEnabled || !this.deposit;
      input.value = this.deposit?.planId || '';
    }

    onSubmit(event) {
      if (event.target !== this.form) return;
      if (this.data?.quoteMode) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.requestOrder(event.submitter || this.order);
        return;
      }
      if (!this.data?.checkoutEnabled) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.addConfigurationToCart();
        return;
      }
      this.deposit = this.model.getDeposit(this.find(), this.data.planId);
      this.applyPlan();
      const submitted = new FormData(this.form);
      const selectedPlans = submitted.getAll('selling_plan');
      const selectedIds = submitted.getAll('id');
      const quantities = submitted.getAll('quantity');
      if (!this.variant?.available || !this.deposit || selectedPlans.length !== 1 || String(selectedPlans[0]) !== String(this.deposit.planId) || selectedIds.length !== 1 || String(selectedIds[0]) !== String(this.find()?.id) || quantities.length !== 1 || String(quantities[0]) !== '1') {
        event.preventDefault(); event.stopImmediatePropagation();
        this.showError('Unable to confirm the deposit for this configuration. Please contact us to place your order.');
      }
      if (event.defaultPrevented) return;
      // Preserve the configurator presentation on Impact's native cart path.
      const properties = {
        '_kindra_config': 'true',
        '_Delivery month': (this.deliveryAssembly?.stateWindow?.(false) || '').replace(/\b20\d{2}\b/g, '').trim(),
        '_Cladding finish': this.label(this.state.finish),
        '_Heater': this.heaterName(this.state.heater) + ' · ' + this.state.power + 'kW',
        '_Assembly': this.assemblyLabel(this.state.assembly)
      };
      this.form.querySelectorAll('[data-ke-cart-property]').forEach(input => input.remove());
      Object.entries(properties).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = `properties[${key}]`; input.value = value;
        input.setAttribute('data-ke-cart-property', ''); this.form.append(input);
      });
      if (this.deliveryAssembly?.stateMode && this.data.currency === 'AUD') {
        const state = this.deliveryAssembly.input?.value || '';
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = 'attributes[Kindra delivery state]';
        input.value = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].includes(state) ? state : '';
        input.setAttribute('data-ke-cart-property', ''); this.form.append(input);
      }
      // A verified plan is submitted through Impact's standard product-form.
    }

    requestOrder(trigger = document.activeElement) {
      if (this.order.disabled) return;
      if (this.data.quoteMode) {
        this.updateQuote();
        if (!this.quoteDialog.open) this.openDialog(this.quoteDialog, trigger);
        return;
      }
      // Once deposit verification is complete, every purchase entry point
      // uses Impact's real add-to-cart flow and its header-count events.
      if (this.data.checkoutEnabled) this.form.requestSubmit(this.order);
      else this.addConfigurationToCart();
    }


    openPurchaseEnquiry(trigger = document.activeElement) {
      if (!this.data.purchaseEnquiry || !this.quoteDialog || !this.variant || this.variant.previewOnly) return;
      this.updateQuote();
      if (!this.quoteDialog.open) this.openDialog(this.quoteDialog, trigger);
    }

    showPurchaseOptions() {
      const actions = this.querySelector('[data-ke-purchase-actions]');
      if (!actions) return;
      actions.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'center' });
      actions.querySelector('[data-ke-enquire]')?.focus({ preventScroll: true });
    }

    setupQuote() {
      if ((!this.data.quoteMode && !this.data.purchaseEnquiry) || !this.quoteDialog) return;
      const form = this.quoteDialog.querySelector('form');
      this.quoteDialog.querySelectorAll('[data-ke-quote-close]').forEach(button => button.addEventListener('click', () => this.quoteDialog.close(), { signal: this.abort.signal }));
      this.quoteDialog.querySelector('[data-ke-quote-edit]')?.addEventListener('click', () => {
        this.returnFocus = this.querySelector('[data-ke-finish][aria-pressed="true"]');
        this.quoteDialog.close();
        this.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
      }, { signal: this.abort.signal });
      form?.addEventListener('submit', event => {
        if (this.data.editorMode) {
          event.preventDefault();
          this.quoteDialog.querySelector('[data-ke-quote-status]').textContent = this.data.purchaseEnquiry ? this.data.enquiryText.editor : 'Preview only. Open the storefront preview to send a quote request.';
          return;
        }
        if (!this.variant) { event.preventDefault(); return; }
        this.updateQuote();
        const button = form.querySelector('[type="submit"]');
        const status = this.quoteDialog.querySelector('[data-ke-quote-status]');
        if (button) {
          button.setAttribute('aria-busy', 'true');
          button.textContent = 'Sending…';
        }
        if (status) status.textContent = this.data.purchaseEnquiry ? this.data.enquiryText.sending : 'Submitting your quote request…';
        // Keep the submit control enabled for Shopify's CAPTCHA retry.
        const reset = () => {
          if (button) {
            button.removeAttribute('aria-busy');
            button.textContent = button.dataset.keQuoteLabel;
          }
        };
        window.addEventListener('pageshow', reset, { once: true, signal: this.abort.signal });
        window.setTimeout(() => {
          if (!this.isConnected) return;
          reset();
          if (status) status.textContent = 'Your request is still processing. Please wait before submitting again.';
        }, 30000);
        // Shopify handles validation, spam protection and delivery. Never infer success from a click.
      }, { signal: this.abort.signal });
      if (this.quoteDialog.querySelector('[data-ke-quote-result]')) this.openDialog(this.quoteDialog, this.querySelector('[data-ke-enquire]') || this.order);
    }

    updateQuote() {
      if (!this.quoteDialog || !this.variant) return;
      if (!this.data.purchaseEnquiry) {
        this.deliveryAssembly?.refresh();
        this.deliveryAssembly?.updateQuote();
      }
      const assembly = this.assemblyLabel(this.state.assembly) + (this.data.quoteAssembly && this.state.assembly ? ' — quoted separately' : '');
      const lines = [
        this.querySelector('h1')?.textContent.trim() || this.data.title,
        `Cladding finish: ${this.label(this.state.finish)}`,
        `Heater: ${this.heaterName(this.state.heater)} · ${this.state.power} kW`,
        `Assembly: ${assembly}`,
        `Configuration subtotal: ${this.money(this.variant.price)} ${this.data.currency}`,
        'Delivery: to be confirmed in your quote'
      ];
      const amounts = this.data.purchaseEnquiry ? this.configurationAmounts() : null;
      const deliveryState = this.deliveryAssembly?.parentController ? this.deliveryAssembly.input?.value : this.deliveryResult?.destination?.code;
      let deliveryText;
      if (amounts) {
        const text = this.data.enquiryText;
        deliveryText = amounts.delivery === null ? text.unconfirmed : amounts.delivery === 0 ? text.complimentary : `${this.money(amounts.delivery)} ${this.data.currency}`;
        lines.splice(4, 2,
          'Quantity: 1',
          `${text.state || 'Delivery state'}: ${deliveryState || text.not_selected}`,
          `${text.subtotal}: ${this.money(amounts.subtotal)} ${this.data.currency}`,
          `Delivery: ${deliveryText}`,
          `${amounts.delivery === null ? text.subtotal : text.total}: ${this.money(amounts.total)} ${this.data.currency}`);
      }
      const summary = this.quoteDialog.querySelector('[data-ke-quote-summary]');
      if (summary) summary.textContent = lines.join('\n');
      const fields = {
        title: lines[0], finish: this.label(this.state.finish),
        heater: `${this.heaterName(this.state.heater)} · ${this.state.power} kW`,
        assembly,
        price: `${this.money(amounts ? amounts.subtotal : this.variant.price)} ${this.data.currency}`,
        ...(amounts ? {
          state: deliveryState || this.data.enquiryText.not_selected,
          delivery: deliveryText,
          total: `${this.money(amounts.total)} ${this.data.currency}`,
          'total-label': amounts.delivery === null ? this.data.enquiryText.subtotal : this.data.enquiryText.total
        } : {})
      };
      Object.entries(fields).forEach(([key, value]) => {
        const element = this.quoteDialog.querySelector(`[data-ke-quote-${key}]`);
        if (element) element.textContent = value;
      });
      const image = this.quoteDialog.querySelector('[data-ke-quote-image]');
      const imageData = this.quoteDialog.querySelector('[data-ke-quote-images]')?.textContent;
      if (image && imageData) {
        const source = JSON.parse(imageData)[this.variant.id];
        if (source) { image.src = source; image.alt = `${fields.title} — ${fields.finish}`; }
      }
      const configuration = this.quoteDialog.querySelector('[name="contact[Configuration]"]');
      if (configuration) configuration.value = lines.join('\n');
      const returnTo = this.quoteDialog.querySelector('[name="return_to"]');
      if (returnTo) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        if (this.data.purchaseEnquiry) url.searchParams.set('ke_state', deliveryState || '');
        // This is the success redirect target, never the current page before submission.
        // Shopify's contact form uses this parameter to render posted_successfully?.
        url.searchParams.set('contact_posted', 'true');
        url.hash = this.quoteDialog.querySelector('form').id;
        returnTo.value = url.pathname + url.search + url.hash;
      }
    }

    async addConfigurationToCart() {
      if (this.data.quoteMode) { this.requestOrder(); return; }
      if (this.cartSubmitting) return;
      const variant = this.find();
      if (!variant?.available || variant.previewOnly) {
        this.showError('This configuration is not yet available in the Shopify catalog. Please choose an available configuration or contact us.');
        return;
      }
      if (!this.data.visualPreview) {
        this.showError('Online ordering is not yet enabled. Please contact us.');
        return;
      }
      this.cartSubmitting = true;
      this.order.setAttribute('aria-busy', 'true');
      this.showError('');
      const root = window.Shopify?.routes?.root || '/';
      try {
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const properties = {
          '_kindra_review': 'true',
          'Cladding finish': this.label(this.state.finish),
          'Heater': this.heaterName(this.state.heater) + ' · ' + this.state.power + 'kW',
          'Assembly': this.assemblyLabel(this.state.assembly)
        };
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties }], sections, sections_url: window.location.pathname })
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || 'Unable to add your configuration.');
        const cartResponse = await fetch(root + 'cart.js');
        if (!cartResponse.ok) throw new Error('Your item was added, but the cart could not refresh. Open the cart to review it.');
        const cart = await cartResponse.json();
        cart.sections = added.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart } }));
        if (window.themeVariables?.settings?.cartType === 'page') window.location.href = root + 'cart';
      } catch (error) {
        this.showError(error.message || 'Unable to add your configuration. Please try again.');
      } finally {
        this.cartSubmitting = false;
        this.order.removeAttribute('aria-busy');
      }
    }

    openSummary(trigger = document.activeElement) {
      if (!this.variant?.available || this.dialog.open) return;
      this.readLegacyDelivery();
      this.summaryExpanded = true;
      this.renderSummary();
      this.openDialog(this.dialog, trigger);
    }

    openDialog(dialog, trigger) {
      this.returnFocus = trigger;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      this.syncForms();
      this.updateMobileSticky();
      // Focus the heading for context without painting a pointer-triggered ring
      // around Close. Tab still reaches Close with its normal visible outline.
      const heading = dialog.querySelector('h2');
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }

    renderHeatTimes() {
      const selected = this.state.heater + this.state.power;
      this.querySelectorAll('[data-ke-heat-cell]').forEach(cell => {
        const key = cell.dataset.keHeatCell;
        const benchmark = this.data.benchmarks[key]?.trim() || '';
        // Read the same editor settings as the power tiles; never duplicate estimates.
        cell.textContent = benchmark.replace(/\s+to\s+85\s*°\s*c\s*$/i, '') || '—';
        const current = key === selected;
        cell.toggleAttribute('data-selected', current);
        if (current) cell.setAttribute('aria-label', `${benchmark || 'No benchmark'} — your selection`);
        else cell.removeAttribute('aria-label');
      });
    }

    renderComparison() {
      this.renderHeatTimes();
    }

    setupForms() {
      const id = this.dataset.keFormsId?.trim();
      if (!id || !/^[0-9]+$/.test(id)) return;
      const connect = () => {
        if (!this.isConnected || this.formsHost?.isConnected) return;
        const host = document.getElementById(`app-embed-container-${id}`);
        if (!host?.shadowRoot) return;
        this.formsHost = host;
        clearInterval(this.formsDiscoveryTimer);
        this.formsDiscovery?.disconnect();
        // App-owned data attributes, not its generated class names. Observe
        // presentation state only; Forms still owns consent and submissions.
        this.formsObserver = new MutationObserver(() => this.syncForms());
        this.formsObserver.observe(host.shadowRoot, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-current-step', 'aria-label'] });
        this.syncForms();
      };
      this.formsDiscovery = new MutationObserver(connect);
      this.formsDiscovery.observe(document.body, { childList: true, subtree: true });
      // Shadow-root attachment does not trigger the body observer. Retry briefly
      // for delayed app hydration, keeping the contact fallback usable meanwhile.
      let attempts = 0;
      this.formsDiscoveryTimer = setInterval(() => {
        connect();
        if (++attempts >= 30) clearInterval(this.formsDiscoveryTimer);
      }, 1000);
      connect();
      customElements.whenDefined('shopify-forms-embed').then(connect);
    }

    syncForms() {
      const host = this.formsHost;
      if (!host) return;
      const root = host.shadowRoot?.querySelector('[data-layout][data-current-step]');
      const teaser = host.shadowRoot?.querySelector('[data-testid="form-teaser"]');
      const offer = this.querySelector('[data-ke-offer]');
      const isTeaser = root?.dataset.currentStep === 'tease' && !!teaser;
      if (offer) {
        offer.hidden = false;
        offer.querySelector('[data-ke-offer-open]').hidden = !isTeaser;
        offer.querySelector('[data-ke-offer-fallback]').hidden = isTeaser;
      }
      // The floating tray owns its teaser state, including opening the popup.
      // The inline and floating actions share the tray's asynchronous opening guard.
      if (!document.querySelector('kindra-welcome-offer')) {
        host.toggleAttribute('data-ke-managed-teaser', isTeaser);
      }
      host.toggleAttribute('data-ke-suspended', this.dialog.open || this.compareDialog.open || this.assemblyDialog.open || !!this.deliveryDialog?.open || !!this.sizesDialog?.open || this.finishesDialog.open || !!this.quoteDialog?.open);
      const wasOpen = this.formsOpen;
      this.formsOpen = !!root && !isTeaser && !!host.shadowRoot.querySelector('[role="dialog"]');
      if (wasOpen && isTeaser && !this.dialog.open && !this.compareDialog.open && !this.assemblyDialog.open && !this.deliveryDialog?.open && !this.sizesDialog?.open && !this.finishesDialog.open) this.querySelector('[data-ke-offer-open]')?.focus({ preventScroll: true });
      this.updateMobileSticky();
    }

    renderSummary() {
      this.updateConfigurationPrice();
      const body = this.querySelector('[data-ke-summary]');
      body.replaceChildren();
      this.setText('[data-ke-summary-status]', '');
      this.setText('[data-ke-summary-title]', 'Your Configuration');
      this.querySelector('[data-ke-summary-footer]').hidden = false;

      const product = document.createElement('div'); product.className = 'ke__summary-product';
      const expectedFinish = this.galleryFinish(this.state.finish).toLowerCase();
      const visibleMedia = Array.from(this.querySelectorAll('[data-keg-item]')).filter(item => item.dataset.finish === expectedFinish || item.dataset.finish === 'all');
      const featured = visibleMedia.find(item => String(item.dataset.mediaId) === String(this.variant.featured_media?.id));
      const sourceImage = featured?.querySelector('img') || visibleMedia.map(item => item.querySelector('img')).find(Boolean);
      if (sourceImage) {
        const image = document.createElement('img'); image.className = 'ke__summary-image';
        image.src = sourceImage.currentSrc || sourceImage.src; image.alt = `${this.data.title} in ${this.label(this.state.finish)} cladding`;
        image.width = 82; image.height = 100; product.append(image);
      }
      const identity = document.createElement('div'); identity.className = 'ke__summary-identity';
      const title = document.createElement('h3'); title.textContent = this.data.title;
      const finish = document.createElement('p'); finish.textContent = `${this.label(this.state.finish)} cladding`;
      const actions = document.createElement('div'); actions.className = 'ke__summary-actions';
      [['Edit', 'data-ke-edit-sauna'], ['Remove', 'data-ke-remove-sauna']].forEach(([label, attribute]) => {
        const action = document.createElement('button'); action.type = 'button'; action.textContent = label; action.setAttribute(attribute, '');
        action.setAttribute('aria-label', `${label} ${this.data.title}`); actions.append(action);
      });
      identity.append(title, finish, actions); product.append(identity); body.append(product);

      const items = document.createElement('dl'); items.className = 'ke__summary-items';
      const addLine = (label, amount, removeAttribute, removeLabel) => {
        const line = document.createElement('div'); line.className = 'ke__summary-line';
        const name = document.createElement('dt'); name.textContent = label;
        const price = document.createElement('dd'); price.textContent = typeof amount === 'number' ? this.money(amount) : amount;
        if (removeAttribute) {
          const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = removeLabel || 'Remove'; remove.setAttribute(removeAttribute, ''); price.append(remove);
        }
        line.append(name, price); items.append(line);
        return line;
      };
      const base = this.find({ heater: 'harvia', power: this.basePower('harvia'), assembly: false });
      const withoutAssembly = this.find({ assembly: false });
      const hasBreakdown = !!(base && withoutAssembly);
      addLine(hasBreakdown ? 'Sauna & finish' : 'Sauna configuration', hasBreakdown ? base.price : this.variant.price);
      const upgrade = hasBreakdown ? withoutAssembly.price - base.price : 0;
      const heaterLine = addLine(`${this.heaterName(this.state.heater)} · ${this.state.power}kW`, upgrade ? this.increment(withoutAssembly.price, base.price) : hasBreakdown ? 'Included' : 'Included in configuration');
      heaterLine.dataset.keSummaryLine = 'heater';
      if (this.data.expertAssemblyAvailable !== false) addLine(this.assemblyLabel(true), this.state.assembly ? (hasBreakdown ? this.variant.price - withoutAssembly.price : 'Included in configuration') : 'Not selected');
      if (this.total !== this.variant.price) addLine('Deposit plan adjustment', this.total - this.variant.price);
      addLine('5 Year Sauna Warranty', 'Included');
      addLine(this.state.heater === 'huum' ? '3 Year HUUM Warranty' : '2 Year Harvia Warranty', 'Included');
      this.appendDeliverySummary(addLine);
      const breakdown = document.createElement('details'); breakdown.className = 'ke__summary-breakdown';
      const heading = document.createElement('summary'); heading.textContent = 'Configuration details';
      breakdown.open = !!this.summaryExpanded;
      breakdown.addEventListener('toggle', () => {
        if (breakdown.isConnected) this.summaryExpanded = breakdown.open;
      });
      breakdown.append(heading, items); body.append(breakdown);

      const totals = this.querySelector('[data-ke-summary-totals]'); totals.replaceChildren();
      const amounts = this.configurationAmounts();
      const rows = [['30% deposit', amounts.due, 'ke__due'], [amounts.delivery === null ? 'Configuration subtotal' : 'Configuration total', amounts.total, 'ke__order-value'], ['Balance before delivery', amounts.saunaBalance, 'ke__balance']];
      if (amounts.delivery > 0) rows.push(['Delivery · payment confirmed separately', amounts.delivery, 'ke__balance']);
      if (amounts.delivery === null) rows.push(['Delivery', this.deliveryPending ? 'Updating…' : 'To be confirmed', 'ke__balance']);
      rows.forEach(([label, value, className]) => {
        const row = document.createElement('div'); row.className = className;
        const text = document.createElement('span'); text.textContent = label;
        const amount = document.createElement(className === 'ke__due' ? 'strong' : 'span'); amount.textContent = typeof value === 'number' ? this.money(value) : value;
        if (typeof value === 'number') { const currency = document.createElement('small'); currency.textContent = ` ${this.data.currency}`; amount.append(currency); }
        row.append(text, amount); totals.append(row);
      });
      this.updateInvoiceLink();
    }

    updateInvoiceLink() {
      const link = this.querySelector('[data-ke-invoice]');
      if (!link) return;
      const lines = ['Hi Kindra,', '', "I'd like to arrange an invoice for the configuration below. Please confirm the details and next steps.", '', this.data.title];
      lines.push(`Finish: ${this.label(this.state.finish)}`);
      this.querySelectorAll('.ke__summary-line').forEach(row => {
        // The first summary renders while its dialog is hidden. innerText then
        // loses the delivery line break, so read the explicit text/BR structure.
        const label = Array.from(row.querySelector('dt').childNodes).map(node => node.nodeName === 'BR' ? ' · ' : node.textContent).join('');
        lines.push(`${label}: ${row.querySelector('dd').textContent}`);
      });
      const amounts = this.configurationAmounts();
      lines.push('', `${amounts.delivery === null ? 'Configuration subtotal' : 'Configuration total (including delivery)'}: ${this.money(amounts.total)} ${this.data.currency}`, `30% sauna deposit: ${this.money(amounts.due)} ${this.data.currency}`, `Sauna balance before delivery: ${this.money(amounts.saunaBalance)} ${this.data.currency}`);
      if (amounts.delivery > 0) lines.push(`Delivery included in total: ${this.money(amounts.delivery)} ${this.data.currency}. Please confirm when this is payable.`);
      if (amounts.delivery === null) lines.push('Delivery is not included in the subtotal and needs confirmation.');
      const productUrl = new URL(this.data.url, window.location.origin);
      if (!this.variant.previewOnly) productUrl.searchParams.set('variant', this.variant.id);
      else productUrl.searchParams.delete('variant');
      lines.push('', `Product: ${productUrl.href}`, '', 'My name:', 'Delivery suburb and postcode:', 'Phone (optional):', '', 'Thank you');
      link.href = `mailto:${link.dataset.email}?subject=${encodeURIComponent('Invoice request — Elata sauna')}&body=${encodeURIComponent(lines.join('\r\n'))}`;
    }

    showSummaryState(title, message, actionLabel, actionAttribute, deposit) {
      this.setText('[data-ke-summary-title]', title);
      this.setText('[data-ke-summary-status]', '');
      this.querySelector('[data-ke-summary-footer]').hidden = true;
      const body = this.querySelector('[data-ke-summary]'); body.replaceChildren();
      const state = document.createElement('div'); state.className = 'ke__summary-state';
      if (typeof deposit === 'number') {
        const label = document.createElement('p'); label.textContent = 'Your deposit';
        const amount = document.createElement('p'); amount.className = 'ke__preview-amount'; amount.textContent = `${this.money(deposit)} ${this.data.currency}`;
        state.append(label, amount);
      }
      const text = document.createElement('p'); text.textContent = message;
      const back = document.createElement('button'); back.type = 'button'; back.textContent = actionLabel; back.className = 'ke__primary'; back.setAttribute(actionAttribute, '');
      state.append(text, back); body.append(state); back.focus();
    }

    updateMobileSticky() {
      if (this.data?.purchaseVisible === false || this.data?.stickyEnabled === false || document.querySelector('kindra-welcome-offer')) {
        this.querySelector('[data-ke-sticky]').hidden = true;
        return;
      }
      const box = (this.querySelector('[data-ke-purchase-actions]') || this.order).getBoundingClientRect();
      const visible = box.top < window.innerHeight + 12 && box.bottom > -12;
      this.querySelector('[data-ke-sticky]').hidden = visible || this.dialog.open || this.compareDialog.open || this.assemblyDialog.open || !!this.deliveryDialog?.open || !!this.sizesDialog?.open || this.finishesDialog.open || !!this.quoteDialog?.open || this.formsOpen || !this.variant;
    }

    updateStickyPosition() {
      const panel = this.querySelector('[data-ke-panel]');
      const header = document.querySelector('#shopify-section-header, .shopify-section--header');
      const visibleHeader = header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
      panel.style.setProperty('--ke-sticky-top', `${Math.min(visibleHeader + 20, window.innerHeight - panel.offsetHeight - 20)}px`);
      this.updateMobileSticky();
    }
    restoreScroll() {
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        delete this.previousOverflow;
      }
    }
  }
  customElements.define('kindra-elata-product', KindraElataProduct);
})();







